import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { Course } from '../types';
import { getDisplayColor } from '../utils/courseColors';
import { normalizeAcademicYear } from '../utils/courseIdentity';
import { useSyllabusCatalog } from '../hooks/useSyllabusCatalog';
import { expandSlotBasedSyllabusRows } from '../utils/syllabusData';

interface SyllabusContextState {
  syllabusCourses: Course[];
  isLoading: boolean;
  error: Error | null;
  reloadSyllabus: () => void;
  ensureSyllabusLoaded: () => void;
}

const SyllabusContext = createContext<SyllabusContextState | undefined>(undefined);

interface SyllabusProviderProps {
  children: ReactNode;
}

export const SyllabusProvider: React.FC<SyllabusProviderProps> = ({ children }) => {
  const [loadVersion, setLoadVersion] = useState(0);
  const { catalog, loading: catalogLoading, valid: catalogValid } = useSyllabusCatalog(loadVersion ? 'all' : '');
  const [syllabusCourses, setSyllabusCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const ensureSyllabusLoaded = useCallback(() => setLoadVersion(version => version || 1), []);

  useEffect(() => {
    if (!loadVersion) return;
    let cancelled = false;
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Keep the large catalog out of the startup bundle and load it only when a syllabus screen opens.
        const [productionModule, autumnModule, developmentModule] = await Promise.all([
          import('../data/allSyllabusData.json'),
          import('../data/Syllabus_2026_Autumn.json'),
          __DEV__ ? import('../data/developmentSyllabusData.json') : Promise.resolve({ default: [] }),
        ]);
        const productionCoursesRaw = productionModule.default;
        const autumnCoursesRaw = autumnModule.default;
        const developmentCoursesRaw = developmentModule.default;

        if (!Array.isArray(productionCoursesRaw)
          || !Array.isArray(autumnCoursesRaw)
          || !Array.isArray(developmentCoursesRaw)) {
          throw new Error('Invalid syllabus data format');
        }

        const allCoursesBase = (__DEV__
          ? [...developmentCoursesRaw, ...productionCoursesRaw]
          : productionCoursesRaw).concat(expandSlotBasedSyllabusRows(autumnCoursesRaw)) as Course[];
        const uniqueCourses = new Map<string, Course>();
        const productionCourseCount = __DEV__
          ? developmentCoursesRaw.length + productionCoursesRaw.length
          : productionCoursesRaw.length;
        allCoursesBase.slice(0, productionCourseCount).forEach((course) => {
          const year = normalizeAcademicYear(course.academicYear);
          if (!course.id || !year) return;
          const key = `${year}_${course.id}`;
          if (!uniqueCourses.has(key)) uniqueCourses.set(key, course);
        });
        allCoursesBase.slice(productionCourseCount).forEach((course) => {
          const year = normalizeAcademicYear(course.academicYear);
          if (!course.id || !year) return;
          const key = `${year}_${course.id}_${course.semester || ''}_${course.dayOfWeek ?? ''}_${course.period ?? ''}`;
          if (!uniqueCourses.has(key)) uniqueCourses.set(key, course);
        });

        // Apply colors and restore syllabus links at runtime.
        const data = Array.from(uniqueCourses.values()).map(course => {
          const year = course.academicYear
            ? normalizeAcademicYear(course.academicYear)
            : '2025';
          return {
            ...course,
            color: getDisplayColor(course),
            syllabusUrl: `https://gakumu-web1.tufs.ac.jp/Portal/Public/Syllabus/DetailMain.aspx?lct_year=${year}&lct_cd=${course.id}`
          };
        });
        if (!cancelled) setSyllabusCourses(data);
        console.log(`SyllabusContext: Successfully loaded ${data.length} courses`);
      } catch (e) {
        console.error('SyllabusContext: Error loading syllabus data:', e);
        const errorMessage = e instanceof Error ? e.message : 'Unknown error';
        if (!cancelled) setError(new Error(`シラバスデータの読み込みに失敗しました: ${errorMessage}`));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [loadVersion]);

  useEffect(() => {
    if (loadVersion && !catalogLoading && !catalogValid) console.warn('[SyllabusContext] syllabusCatalog/all is missing or invalid');
    if (catalog) console.log(`[SyllabusContext] catalog/all verified: ${catalog.count} ids (${catalog.syncVersion})`);
  }, [catalog, catalogLoading, catalogValid, loadVersion]);

  const reloadSyllabus = () => {
    setLoadVersion(version => version + 1);
  };

  return (
    <SyllabusContext.Provider value={{ syllabusCourses, isLoading, error, reloadSyllabus, ensureSyllabusLoaded }}>
      {children}
    </SyllabusContext.Provider>
  );
};

export const useSyllabus = (loadOnMount = true): SyllabusContextState => {
  const context = useContext(SyllabusContext);
  useEffect(() => {
    if (loadOnMount) context?.ensureSyllabusLoaded();
  }, [context?.ensureSyllabusLoaded, loadOnMount]);
  if (context === undefined) {
    throw new Error('useSyllabus must be used within a SyllabusProvider');
  }
  return context;
};
